import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  notFound,
  TaxType,
} from "Components/constants/Common";
import { viewOrderApi } from "Api/Order";
import TableContainer from "Components/Base/BaseTable";
import { RiSearchLine } from "react-icons/ri";
import moment from "moment";
import {
  DESTINATION,
  DESTINATION_KEY,
  FINAL_AMOUNT_LABEL,
  LR_COPY,
  LR_COPY_KEY,
  LR_DATE,
  LR_DATE_KEY,
  LR_NO,
  LR_NO_KEY,
  MODE_OF_TRANSACTION_KEY,
  MODE_OF_TRANSPORTATION,
  ORDER_STATUS_KEY,
  ORDER_TOTAL_LABEL,
  ORIGIN,
  ORIGIN_KEY,
  OUTSTANDING_AMOUNT,
  OUTSTANDING_AMOUNT_KEY,
  PAYABLE_AMOUNT,
  PAYABLE_AMOUNT_KEY,
  PAYMENT_DETAILS_LABEL,
  PAYMENT_MODE,
  PAYMENT_MODE_KEY,
  PRODUCT_CODE_KEY,
  RATE_KEY,
  RECEIVED_AMOUNT,
  RECEIVED_DATE,
  REFUND_DETAILS_LABEL,
  REMARKS_KEY,
  REMARKS_LABEL,
  RETURNED_DETAILS_LABEL,
  SHIPMENT_DATE_LABEL,
  TAX_KEY,
  TAX_LABEL,
  TRANSPORT_DETAILS_LABEL,
  VEHICLE_NO,
  VEHICLE_NO_KEY,
  VIEW_ORDER_LABEL,
} from "Components/constants/Order";
import {
  AmountLabel,
  BatchNoLabel,
  OrderDateLabel,
  ProductCodeLabel,
  QuantityKey,
  QuantityLabel,
  RateLabel,
  ReceivedDateKey,
  ReceivedDateLabel,
} from "Components/constants/PurchaseOrder";
import { CustomerNameLabel, ViewFileLabel } from "Components/constants/Staff";
import { StatusLabel } from "Components/constants/Role";
import { BaseImageURL } from "Api/services";

interface OrderHistory {
  id: number;
  order_id: number;
  total_amount: number;
  received_amount: number;
  out_standing_amount: number;
  payable_amount: number;
  received_date: string;
  payment_mode: string;
  remarks: string;
}
interface OrderItem {
  quantity: number;
  rate: number;
  tax: string;
  final_amount: number;
  order_status: string;
  Product: {
    id: number;
    product_code: string;
  };
}

interface OrderTransaction {
  id: number;
  order_id: number;
  mode_of_transaction?: string;
  origin?: string;
  destination?: string;
  vehicle_no?: string;
  lr_no?: string;
  lr_copy?: string;
  lr_date?: string;
}

interface History {
  id: number;
  order_draft_id?: string;
  order_type: string;
  order_date: string;
  expected_shipment_date: string;
  discount_value: number;
  total_price: number;
  payment_status: string;
  authUser: {
    id: number;
    name: string;
  };
  project: {
    id: number;
    project_name: string;
  };
  orderItem?: OrderItem[];
  orderHistory?: OrderHistory[];
  orderRefund?: any;
  orderTransaction?: OrderTransaction;
}

const ViewOrder = () => {
  document.title = VIEW_ORDER_LABEL;
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [history, setHistory] = useState<History>();

  const fetchProduct = () => {
    setLoader(true);
    viewOrderApi(Number(id))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setHistory(resp?.data);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const orderItemColumns = useMemo(
    () => [
      {
        header: ProductCodeLabel,
        accessorKey: PRODUCT_CODE_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: RateLabel,
        accessorKey: RATE_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.rate}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: BatchNoLabel,
        accessorKey: BatchNoLabel,
        cell: (cell: any) => {
          return (
            <span>
               {cell?.row?.original?.productVariant?.batch_no}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: "Make",
        accessorKey: "Make",
        cell: (cell: any) => {
          return (
            <span>
              {cell?.row?.original?.productVariant?.manufacturer?.name}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: QuantityLabel,
        accessorKey: QuantityKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Order Return Status",
        accessorKey: ORDER_STATUS_KEY,
        cell: (cell: any) => {
          const statusColorMap: { [key: string]: string } = {
            Pending: "bg-warning",
            "Partially Paid": "bg-info",
            Paid: "bg-success",
            "Partially Returned": "bg-primary",
            "Returned": "bg-danger",
          };
          const paymentStatus = cell?.row?.original?.order_status;

          return (
            <div>
              <span className={`badge ${statusColorMap[paymentStatus]}`}>
                {cell?.row?.original?.order_status}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TAX_LABEL,
        accessorKey: TAX_KEY,
        enableSorting: true,
        cell: (cell: any) => {
          let tax: any = TaxType.find((item: any) => {
            return item.value === cell?.row?.original?.tax;
          });
          return <span>{tax?.label}</span>;
        },
        enableColumnFilter: false,
      },
      {
        header: AmountLabel,
        accessorKey: FINAL_AMOUNT_LABEL,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.final_amount.toFixed(2)}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    []
  );
  const orderColumns = useMemo(
    () => [
      {
        header: PAYABLE_AMOUNT,
        accessorKey: PAYABLE_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.payable_amount}
            </span>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: RECEIVED_AMOUNT,
        accessorKey: "received_amount",
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.received_amount}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: OUTSTANDING_AMOUNT,
        accessorKey: OUTSTANDING_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.out_standing_amount}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ReceivedDateLabel,
        accessorKey: ReceivedDateKey,
        cell: (cell: any) => {
          return (
            <div>
              <span>
                {moment(cell?.row?.original?.received_date).format("ll")}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PAYMENT_MODE,
        accessorKey: PAYMENT_MODE_KEY,
        cell: (cell: any) => {
          return (
            <div>
              <span className="badge bg-dark">
                {cell?.row?.original?.payment_mode}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: REMARKS_LABEL,
        accessorKey: REMARKS_KEY,
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    []
  );
  const orderRefundColumns = useMemo(
    () => [
      {
        header: ProductCodeLabel,
        accessorKey: PRODUCT_CODE_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: RateLabel,
        accessorKey: RATE_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.rate}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: "Order Quantity",
        accessorKey: "received_quantity",
        enableColumnFilter: false,
      },
      {
        header: "Return Quantity",
        accessorKey: "return_quantity",
        enableColumnFilter: false,
      },
      {
        header: "Return Date",
        accessorKey: "return_date",
        cell: (cell: any) => {
          return (
            <div>
              <span>
                {moment(cell?.row?.original?.return_date).format("ll")}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: AmountLabel,
        accessorKey: "amount",
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.amount}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Paid Amount",
        accessorKey: "refund_amount",
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.refund_amount}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Reason",
        accessorKey: "reason",
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const orderTransactionColumns = useMemo(
    () => [
      {
        header: MODE_OF_TRANSPORTATION,
        accessorKey: MODE_OF_TRANSACTION_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: ORIGIN,
        accessorKey: ORIGIN_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: DESTINATION,
        accessorKey: DESTINATION_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: VEHICLE_NO,
        accessorKey: VEHICLE_NO_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: LR_NO,
        accessorKey: LR_NO_KEY,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: LR_DATE,
        accessorKey: LR_DATE_KEY,
        cell: (cell: any) => {
          return (
            <div>
              <span>{moment(cell?.row?.original?.lr_date).format("ll")}</span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: LR_COPY,
        accessorKey: LR_COPY_KEY,
        cell: (cell: any) => {
          return (
            <div>
              <Link
                target="_blank"
                className="text-link font-weight-bold"
                to={`${BaseImageURL}${cell?.row?.original?.lr_copy}`}
              >
                {ViewFileLabel}
              </Link>
            </div>
          );
        },
        enableColumnFilter: false,
      },
    ],
    []
  );

  const statusColorMap: { [key: string]: string } = {
    Pending: "bg-warning",
    "pending": "bg-warning",
    "Partially Paid": "bg-info",
    Paid: "bg-success",
    "Partially Returned": "bg-primary",
    Returned: "bg-danger",
  };
  const paymentStatus = history?.payment_status;

  useEffect(() => {
    fetchProduct();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">{VIEW_ORDER_LABEL}</span>
          <button className="btn btn-danger" onClick={() => navigate("/order")}>
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card" id="demo">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body py-2 px-4">
                <div className="row g-3">
                  <h5 className="mt-4">
                    Order Id: {history?.order_draft_id || notFound.nullData}
                  </h5>
                  <div className="border-top border-top-dashed mt-2"></div>
                  <div className="col-lg-2 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {CustomerNameLabel}
                    </p>
                    <h5 className="fs-14 mb-0">
                      {history?.authUser?.name || notFound.nullData}
                    </h5>
                  </div>
                  <div className="col-lg-2 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Order Status
                    </p>
                    <span
                      className={`badge ${
                        statusColorMap[String(paymentStatus)]
                       || "bg-warning"} `}
                    >
                      {history?.payment_status || "Pending"}
                    </span>
                  </div>
                  <div className="col-lg-2 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {OrderDateLabel}
                    </p>
                    <h5 className="fs-14 mb-0">
                      {moment(history?.order_date).format("ll")}
                    </h5>
                  </div>
                  <div className="col-lg-2 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {SHIPMENT_DATE_LABEL}
                    </p>
                    <h5 className="fs-14 mb-0">
                      {moment(history?.expected_shipment_date).format("ll")}
                    </h5>
                  </div>
                </div>
                <div className="border-top border-top-dashed mt-2"></div>
                <div className="row g-3 mt-2">
                  {loader ||
                  (history?.orderItem && history?.orderItem?.length > 0) ? (
                    <TableContainer
                      customPageSize={customPageSize}
                      setCustomPageSize={setCustomPageSize}
                      isGlobalFilter={false}
                      data={history?.orderItem || []}
                      columns={orderItemColumns}
                      tableClass="table bg-white"
                      thClass="bg-light"
                      manualPagination={true}
                      manualFiltering={false}
                      tableLoader={loader}
                      hasManualPagination={false}
                      currentPage={1}
                      setCurrentPage={setCurrentPage}
                      totalPages={totalPages}
                      totalRecords={totalRecords}
                      totalNumberOfRows={totalNumberOfRows}
                      fetchData={() => {}}
                      fetchSortingData={() => {}}
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <div>
                        <span className="fs-1 text-success">
                          <RiSearchLine />
                        </span>
                      </div>
                      <div className="mt-4">
                        <h5>{notFound.dataNotFound}</h5>
                        <p className="text-muted">
                          {notFound.thereIsNoDataFound}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="d-flex justify-content-end g-3">
                  <div className="m-2">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {ORDER_TOTAL_LABEL}
                    </p>
                    <h5 className="fs-14 mb-0">
                      <i className="mdi mdi-currency-rupee"></i>
                      {history?.total_price?.toFixed(2)}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
            {history?.orderHistory && history?.orderHistory?.length > 0 && (
              <div className="col-lg-12">
                <div className="card-body py-2 px-4">
                  <div className="border-top border-top-dashed mt-2"></div>
                  <div className="mt-3">
                    <h6 className="text-muted text-uppercase fw-semibold mb-3">
                      {PAYMENT_DETAILS_LABEL}
                    </h6>
                    <div className="table-responsive mt-2">
                      {loader ||
                      (history?.orderHistory &&
                        history?.orderHistory?.length > 0) ? (
                        <TableContainer
                          customPageSize={customPageSize}
                          setCustomPageSize={setCustomPageSize}
                          isGlobalFilter={false}
                          data={history?.orderHistory || []}
                          columns={orderColumns}
                          tableClass="table bg-white"
                          thClass="bg-light"
                          manualPagination={true}
                          manualFiltering={false}
                          tableLoader={loader}
                          hasManualPagination={false}
                          currentPage={1}
                          setCurrentPage={setCurrentPage}
                          totalPages={totalPages}
                          totalRecords={totalRecords}
                          totalNumberOfRows={totalNumberOfRows}
                          fetchData={() => {}}
                          fetchSortingData={() => {}}
                        />
                      ) : (
                        <div className="py-4 text-center">
                          <div>
                            <span className="fs-1 text-success">
                              <RiSearchLine />
                            </span>
                          </div>
                          <div className="mt-4">
                            <h5>{notFound.dataNotFound}</h5>
                            <p className="text-muted">
                              {notFound.thereIsNoDataFound}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {history?.orderRefund && history?.orderRefund?.length > 0 && (
              <div className="col-lg-12">
                <div className="card-body py-2 px-4">
                  <div className="border-top border-top-dashed mt-2"></div>
                  <div className="mt-3">
                    <h6 className="text-muted text-uppercase fw-semibold mb-3">
                      {RETURNED_DETAILS_LABEL}
                    </h6>
                    <div className="table-responsive mt-2">
                      {loader ||
                      (history?.orderRefund &&
                        history?.orderRefund?.length > 0) ? (
                        <TableContainer
                          customPageSize={customPageSize}
                          setCustomPageSize={setCustomPageSize}
                          isGlobalFilter={false}
                          data={history?.orderRefund || []}
                          columns={orderRefundColumns}
                          tableClass="table bg-white"
                          thClass="bg-light"
                          manualPagination={true}
                          manualFiltering={false}
                          tableLoader={loader}
                          hasManualPagination={false}
                          currentPage={1}
                          setCurrentPage={setCurrentPage}
                          totalPages={totalPages}
                          totalRecords={totalRecords}
                          totalNumberOfRows={totalNumberOfRows}
                          fetchData={() => {}}
                          fetchSortingData={() => {}}
                        />
                      ) : (
                        <div className="py-4 text-center">
                          <div>
                            <span className="fs-1 text-success">
                              <RiSearchLine />
                            </span>
                          </div>
                          <div className="mt-4">
                            <h5>{notFound.dataNotFound}</h5>
                            <p className="text-muted">
                              {notFound.thereIsNoDataFound}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {history?.orderTransaction && (
              <div className="col-lg-12">
                <div className="card-body py-2 px-4">
                  <div className="border-top border-top-dashed mt-2"></div>
                  <div className="mt-3">
                    <h6 className="text-muted text-uppercase fw-semibold mb-3">
                      {TRANSPORT_DETAILS_LABEL}
                    </h6>
                    <div className="table-responsive mt-2">
                      {loader || history?.orderTransaction != null ? (
                        <TableContainer
                          customPageSize={customPageSize}
                          setCustomPageSize={setCustomPageSize}
                          isGlobalFilter={false}
                          data={[history?.orderTransaction]}
                          columns={orderTransactionColumns}
                          tableClass="table bg-white"
                          thClass="bg-light"
                          manualPagination={true}
                          manualFiltering={false}
                          tableLoader={loader}
                          hasManualPagination={false}
                          currentPage={1}
                          setCurrentPage={setCurrentPage}
                          totalPages={totalPages}
                          totalRecords={totalRecords}
                          totalNumberOfRows={totalNumberOfRows}
                          fetchData={() => {}}
                          fetchSortingData={() => {}}
                        />
                      ) : (
                        <div className="py-4 text-center">
                          <div>
                            <span className="fs-1 text-success">
                              <RiSearchLine />
                            </span>
                          </div>
                          <div className="mt-4">
                            <h5>{notFound.dataNotFound}</h5>
                            <p className="text-muted">
                              {notFound.thereIsNoDataFound}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOrder;
